/**
 * A booking's page's behavior half (web-account-pages D4): card 2's booking
 * read on the website session, drawn in the Mini App booking detail's states
 * (its pill, when line, notice and payment row, shared through
 * `bookingDetailRows`), the cancel window with the refund rule read fresh as the
 * app reads it, Add to Calendar and directions from card 2, Join, Pay now and
 * Book again. Draws nothing.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { virtualPlatformLabel, webServicePath } from '@telify/shared/browser';
import { formatCurrency } from '@/components/payment/formatCurrency';
import {
  BOOKING_READ_FAILED,
  bookingErrand,
  bookingNotice,
  cancelKindOf,
  derivePaymentRow,
  fmtDayDate,
  fmtTime,
  heroWhenLabel,
  isRequest,
  statusPill,
  type CancelKind,
  type ErrandFacts,
} from '@/pages/BookingDetailPage/bookingDetailRows';
import { useBookingCancelFlow } from '@/hooks/booking/useBookingCancelFlow';
import type { BookingDetail } from '@/types/bookingDetail';
import { formatDuration } from '@/utils/formatDateTime';
import { phoneCallStep } from '@/pages/booking/shared/bookingRules';
import { bookingCalendarLinks, CALENDAR_FILE_FAILED } from '@/utils/calendarLinks';
import { copyPasscode, writeClipboard } from '@/utils/joinVirtual';
import { buildMapsUrl } from '@/utils/mapLinks';

import { SiteFetchError, useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { keepTimes } from '../../format';
import { openCalendarFile, useWebBooking } from '../../hooks/useWebBooking';
import { useWebCancelBooking } from '../../hooks/useWebAccount';
import { useMessagesPanel } from '../../messages/panelContext';
import { usePageMover } from '../../pageMover';
import { bookingPhoto } from '../../ui/Photo';
import {
  contract,
  type BookingPageActions,
  type BookingPageCancelWindow,
  type BookingPageEvents,
  type BookingPageRow,
  type BookingPageState,
} from './contract';

export type BookingPageRuntime = PieceRuntime<typeof contract, BookingPageState, BookingPageActions>;

const SHORT = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const DONE: Record<CancelKind, string> = { booking: 'Booking canceled', request: 'Request canceled', waitlist: 'Waitlist left' };

/**
 * The booking's one action on the website (bookingErrand), at the website's addresses: Complete booking, Pay now, Book
 * again. The phone's booking page and the marks in the website's lists (web-studio-sites 6.6) use it too.
 */
export function bookingPageErrand(
  b: ErrandFacts & Pick<BookingDetail, 'id'> & { readonly service: Pick<BookingDetail['service'], 'id' | 'name' | 'type'> },
): BookingPageState['errand'] {
  const errand = bookingErrand(b);
  if (errand?.kind === 'complete') return { kind: 'complete', label: errand.label, href: `/account/bookings/${encodeURIComponent(b.id)}/complete` };
  if (errand?.kind === 'pay') return { kind: 'payNow', label: errand.label, href: `/account/bookings/${encodeURIComponent(b.id)}/pay` };
  if (errand?.kind === 'bookAgain') {
    return { kind: 'bookAgain', label: errand.label, href: errand.to === 'events' ? '/events' : webServicePath(b.service.id, b.service.name) };
  }
  return null;
}

/** The app's line for the booking's state (BookingDetailPage's notice), in words. */
function noticeOf(b: BookingDetail, studioName: string): BookingPageState['notice'] {
  const line = bookingNotice(b, studioName);
  return line && { icon: line.icon, title: keepTimes(line.title), text: keepTimes(line.text) };
}

function rowsOf(b: BookingDetail): BookingPageRow[] {
  const d = new Date(b.scheduledAt);
  const s = b.status;
  const none = { sub: null, note: null, amount: null, directionsHref: null };
  const rows: BookingPageRow[] = [
    {
      ...none,
      label: s === 'CANCELLED' ? 'Was scheduled' : isRequest(s) ? 'Requested' : 'When',
      value: keepTimes(`${fmtDayDate(d)} · ${fmtTime(d)}`),
    },
    { ...none, label: 'Duration', value: formatDuration(b.service.duration) },
  ];
  const loc = b.location;
  if (s !== 'CANCELLED' && loc.isByPhone) {
    // service-by-phone D14: the call line with the client's current number, no directions and no Join; a request too.
    rows.push({ ...none, label: 'Location', value: 'By phone', sub: phoneCallStep(b.phoneCall)?.line ?? null });
  } else if (s !== 'CANCELLED' && !isRequest(s)) {
    if (loc.isVirtual) {
      const platform = virtualPlatformLabel(loc.virtualPlatform);
      rows.push({ ...none, label: 'Location', value: 'Online', sub: platform ? `on ${platform}` : null });
    } else {
      rows.push({
        ...none,
        label: 'Location',
        value: loc.name ?? 'In person',
        sub: [loc.address, loc.city, loc.state].filter(Boolean).join(', ') || null,
        note: loc.notes,
        directionsHref: buildMapsUrl(loc),
      });
    }
  }
  const pay = derivePaymentRow(b);
  if (pay) {
    rows.push({ ...none, label: 'Payment', value: pay.val, sub: pay.sub ?? null, amount: pay.amount != null ? formatCurrency(pay.amount) : null });
  }
  return rows;
}

export function useBookingPage(input: PieceInput<BookingPageEvents> = {}): BookingPageRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const { detail } = useWebBooking(bookingId);
  const studio = useSiteStudio().data;
  const studioName = studio?.name ?? '';
  const { openMessages } = useMessagesPanel();
  const navigate = usePageMover();
  const cancel = useWebCancelBooking(bookingId);
  const b = detail.data;

  const [problem, setProblem] = useState<string | null>(null);
  // The cancel window's rules are the app's (useBookingCancelFlow); the computer draws them in its own words.
  const flow = useBookingCancelFlow({
    booking: b,
    refetch: detail.refetch,
    cancel,
    onFailed: () => setProblem('This booking could not be cancelled. Please refresh.'),
    onCancelled: () => emit('cancelled', {}),
  });
  const { success, onDone } = flow;
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [success, onDone]);
  const windowOpen = flow.open || flow.pending;
  const policy: BookingPageCancelWindow['policy'] = flow.policy.loading
    ? { state: 'checking', text: 'Checking your refund policy…' }
    : flow.policy.error
      ? { state: 'unavailable', text: "We couldn't check your refund policy right now." }
      : flow.policy.line
        ? { state: 'ready', text: flow.policy.line }
        : null;
  const done = success ? DONE[success] : null;

  const actions = useMemo<BookingPageActions>(
    () => ({
      openCancel: () => {
        setProblem(null);
        flow.onOpen();
      },
      closeCancel: flow.onClose,
      confirmCancel: flow.onConfirm,
      addToCalendar: () => {
        if (b) openCalendarFile(b).catch(() => setProblem(CALENDAR_FILE_FAILED));
      },
      // Copied quietly: the computer page draws no toast.
      copyPasscode: () => void copyPasscode(b?.location.virtualPassword ?? null, { copy: writeClipboard, toast: () => undefined }),
      messageStudio: () => openMessages(),
      open: (href) => navigate(href),
      retry: () => void detail.refetch(),
    }),
    [flow, b, openMessages, navigate, detail],
  );

  const state = useMemo<BookingPageState>(() => {
    const base = {
      eyebrow: '',
      name: '',
      photoUrl: null,
      photo: null,
      withName: null,
      withPhotoUrl: null,
      when: '',
      statusLabel: '',
      tone: 'live' as const,
      notice: null,
      rows: [],
      calendar: null,
      join: null,
      refunds: null,
      preparation: null,
      about: null,
      chat: { heading: '', line: '', logoUrl: null },
      cancelLabel: null,
      cancelClosed: null,
      cancelWindow: null,
      problem: null,
      readError: null,
      done: null,
      errand: null,
      telegramNote: null,
      studioName,
      bookingsHref: '/account/bookings',
    };
    if (!b) {
      if (!detail.isError) return { ...base, status: 'loading' };
      // Not this client's booking is not-found; any other failure offers Try again (row B2b).
      const notFound = detail.error instanceof SiteFetchError && detail.error.status === 404;
      return notFound ? { ...base, status: 'not-found' } : { ...base, status: 'error', readError: BOOKING_READ_FAILED };
    }

    const s = b.status;
    const d = new Date(b.scheduledAt);
    const event = b.service.type === 'event';
    const kind = cancelKindOf(b);
    const ahead = s === 'CONFIRMED' || s === 'PENDING_PAYMENT';
    const pill = statusPill(b);
    const hero = heroWhenLabel(b);
    const pay = derivePaymentRow(b);
    const withWhom = b.provider ? ` with ${b.provider.name}` : '';

    return {
      ...base,
      status: 'ready',
      eyebrow: event ? 'Your event' : isRequest(s) ? 'Your request' : 'Your session',
      name: b.service.name,
      photoUrl: b.service.imageUrl,
      photo: bookingPhoto(b.service),
      withName: b.provider?.name ?? null,
      withPhotoUrl: b.provider?.avatarUrl ?? null,
      when: keepTimes(hero ? hero.charAt(0).toUpperCase() + hero.slice(1) : `${SHORT.format(d)} at ${fmtTime(d)}`),
      statusLabel: pill.label,
      tone: s === 'CANCELLED' ? 'off' : s === 'COMPLETED' ? 'done' : 'live',
      notice: noticeOf(b, studioName),
      rows: rowsOf(b),
      calendar: ahead ? bookingCalendarLinks(b) : null,
      join: s === 'CONFIRMED' && b.location.isVirtual
        ? { href: b.location.virtualLink, passcode: b.location.virtualPassword, platform: virtualPlatformLabel(b.location.virtualPlatform) }
        : null,
      refunds: pay?.ledger
        ? {
            lines: pay.ledger.lines.map((l) => ({ state: l.onItsWay ? 'On its way' : 'Refunded', when: l.when, amount: `−${formatCurrency(l.amount)}` })),
            net: pay.ledger.net != null ? formatCurrency(pay.ledger.net) : null,
          }
        : null,
      preparation: ahead && b.service.preparationNotes
        ? { heading: b.service.preparationNotesLabel || 'How to prepare', html: b.service.preparationNotes }
        : null,
      about: (ahead || s === 'COMPLETED') && b.service.description
        ? { heading: `About this ${event ? 'event' : 'session'}`, html: b.service.description }
        : null,
      chat: { heading: `Questions about this ${event ? 'event' : 'session'}?`, line: `Message ${studioName}.`, logoUrl: studio?.logoUrl ?? null },
      cancelLabel: b.canCancel ? (kind === 'waitlist' ? 'Leave the waitlist' : kind === 'request' ? 'Cancel this request' : 'Cancel this booking') : null,
      cancelClosed: !b.canCancel ? (b.cancelClosedLine ?? null) : null,
      cancelWindow: windowOpen
        ? {
            kicker: kind === 'waitlist' ? 'Leave waitlist' : kind === 'request' ? 'Cancel request' : 'Cancel booking',
            title: kind === 'waitlist' ? 'Leave this waitlist?' : kind === 'request' ? 'Cancel this request?' : 'Cancel this booking?',
            date: fmtDayDate(d),
            rest:
              kind === 'waitlist'
                ? ` ${event ? 'event' : 'session'}${withWhom}. You won't be notified if a spot opens.`
                : ` ${event ? 'event' : 'session'}${withWhom} will be cancelled. This can't be undone.`,
            nudge: event ? null : 'Need a different time instead? Cancel, then pick a new slot from the service page. It takes under a minute.',
            policy,
            keepLabel: kind === 'waitlist' ? 'Stay on waitlist' : kind === 'request' ? 'Keep my request' : 'Keep my booking',
            yesLabel: cancel.isPending
              ? kind === 'waitlist' ? 'Leaving…' : 'Cancelling…'
              : kind === 'waitlist' ? 'Yes, leave waitlist' : kind === 'request' ? 'Yes, cancel request' : 'Yes, cancel booking',
            busy: cancel.isPending,
          }
        : null,
      problem,
      done,
      errand: bookingPageErrand(b),
      telegramNote: s === 'COMPLETED' || s === 'CANCELLED' ? null : `Changes to this booking and your reminders come to your Telegram chat with ${studioName}.`,
    };
  }, [b, detail.isError, detail.error, studioName, studio?.logoUrl, windowOpen, policy, cancel.isPending, problem, done]);

  return { state, actions, options };
}
